// src/redux/settingsPages/helpcenter/helpcenterSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { fetchHelpCategories, searchHelpArticles, submitFeedback } from './helpcenterActions';
import { FaBook, FaQuestionCircle, FaShieldAlt, FaCog } from 'react-icons/fa';

const iconMap = {
  book: FaBook,
  question: FaQuestionCircle,
  shield: FaShieldAlt,
  settings: FaCog,
};

const normalizeCategory = (c, i = 0) => {
  if (!c || typeof c !== 'object') return null;
  const id = String(c.id ?? c.slug ?? `cat-${i}`);
  const title = String(c.title ?? c.name ?? 'Category');
  const description = String(c.description ?? '');
  const articles = Array.isArray(c.articles) ? c.articles.filter(Boolean) : [];
  const Icon = iconMap[(c.iconKey || c.icon || '').toLowerCase?.()] || iconMap.book;
  return { id, title, description, articles, icon: Icon };
};

const bucketSearchResults = (data) => {
  if (Array.isArray(data) && data.length && (data[0]?.title || data[0]?.name || data[0]?.articles)) {
    return data.map((c, i) => normalizeCategory(c, i)).filter(Boolean);
  }
  const articles = (Array.isArray(data) ? data : [])
    .map((x) => (typeof x === 'string' ? x : (x?.title || x?.name)))
    .filter(Boolean);

  return [{
    id: 'search-results',
    title: 'Search Results',
    description: 'Matched help content for your query',
    articles,
    icon: iconMap.question,
  }];
};

const initialState = {
  categories: [],
  searchResults: [],
  loading: false,
  error: null,
  lastQuery: '',
  feedbackStatus: 'idle',
};

const slice = createSlice({
  name: 'helpcenter',
  initialState,
  reducers: {
    clearSearch(state) {
      state.searchResults = [];
      state.lastQuery = '';
    },
    clearHelpError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // categories
      .addCase(fetchHelpCategories.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchHelpCategories.fulfilled, (s, a) => {
        s.loading = false;
        s.categories = (Array.isArray(a.payload) ? a.payload : []).map(normalizeCategory).filter(Boolean);
      })
      .addCase(fetchHelpCategories.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Failed to load help categories';
        s.categories = [];
      })

      // search
      .addCase(searchHelpArticles.pending, (s, a) => {
        s.loading = true;
        s.error = null;
        s.lastQuery = a.meta.arg || '';
      })
      .addCase(searchHelpArticles.fulfilled, (s, a) => {
        s.loading = false;
        s.searchResults = bucketSearchResults(a.payload);
      })
      .addCase(searchHelpArticles.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Search failed';
      })

      // feedback
      .addCase(submitFeedback.pending, (s) => { s.feedbackStatus = 'loading'; })
      .addCase(submitFeedback.fulfilled, (s) => { s.feedbackStatus = 'succeeded'; })
      .addCase(submitFeedback.rejected, (s) => { s.feedbackStatus = 'failed'; });
  },
});

export const { clearSearch, clearHelpError } = slice.actions;
export default slice.reducer;
